import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Input } from '../../components/UI/Input';
import { Button } from '../../components/UI/Button';
import { Card } from '../../components/UI/Card';
import { Mail, Lock, LogIn, AlertCircle } from 'lucide-react';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { login } = useAuth();
  const { success } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);

    try {
      const res = await login(email, password);
      if (res && res.message) {
        success(res.message);
      } else {
        success('¡Bienvenido de nuevo a Quinvoice!');
      }
      navigate('/');
    } catch (err) {
      setErrorMessage(
        err.data?.message || err.message || 'Credenciales inválidas. Por favor revisa tus datos.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6">
        {/* Brand Header */}
        <div className="text-center space-y-2">
          <div className="w-12 h-12 rounded-2xl bg-[#08707A] text-white flex items-center justify-center font-black text-2xl mx-auto shadow-xl shadow-teal-900/20">
            Q
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
            Iniciar Sesión
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Ingresa tus credenciales para acceder a tu panel de Quinvoice
          </p>
        </div>

        {/* Form Card */}
        <Card className="p-8 shadow-xl border-slate-200/80 dark:border-slate-800">
          {errorMessage && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-950/60 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 text-xs flex items-start gap-3">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 font-medium leading-relaxed">{errorMessage}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Correo Electrónico"
              type="email"
              placeholder="tu@correo.com"
              icon={Mail}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />

            <Input
              label="Contraseña"
              type="password"
              placeholder="••••••••"
              icon={Lock}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <div className="pt-2">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={loading}
                icon={LogIn}
                className="w-full bg-[#08707A] hover:bg-[#065B63]"
              >
                Ingresar a Quinvoice
              </Button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800 text-center">
            <p className="text-xs text-slate-600 dark:text-slate-400">
              ¿No tienes una cuenta aún?{' '}
              <Link
                to="/register"
                className="font-bold text-[#08707A] hover:text-[#065B63] dark:text-teal-400 dark:hover:text-teal-300 transition-colors"
              >
                Crear cuenta nueva
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};
